import axios from "axios";
import {
  ObserverDirectoryPage,
  ObserverSearchResult,
  PracticeCategory,
  PublicProfile,
} from "../types/observer";
import { trackAnalyticsEvent } from "./analytics";
import { observerBackendOrigin } from "./runtimeConfig";

const client = axios.create({
  baseURL: observerBackendOrigin + "/api/observer",
});
export const observerApi = {
  profile: async (id: string) => {
    const data = (
      await client.get<PublicProfile>("/profiles/" + encodeURIComponent(id))
    ).data;
    return {
      ...data,
      entity: { ...data.entity, practiceTags: data.entity.practiceTags || [] },
      platformAccounts: data.platformAccounts || [],
      assertions: data.assertions || [],
      sources: data.sources || [],
      offers: data.offers || [],
      transparency: data.transparency || [],
      visibilityChanges: data.visibilityChanges || [],
      reviews: data.reviews || [],
    };
  },
  search: async (q: string) =>
    (
      await client.get<{ results: ObserverSearchResult[] }>("/search", {
        params: { q },
      })
    ).data.results,
  directory: async (category = "", page = 1, pageSize = 24, sort = "most_established") =>
    (
      await client.get<ObserverDirectoryPage>("/directory", {
        params: { category: category || undefined, page, pageSize, sort },
      })
    ).data,
  categories: async () =>
    (
      await client.get<{ categories: PracticeCategory[] }>("/categories")
    ).data.categories,
  nominate: async (payload: unknown) =>
    (await client.post("/nominations", payload)).data,
  selfSubmit: async (payload: unknown) =>
    (await client.post("/self-submissions", payload)).data,
  compare: async (ids: string[]) =>
    (await client.get("/comparisons", { params: { ids: ids.join(",") } })).data,
  shareLink: async (entityId: string, intent: "record" | "feedback") =>
    (await client.post(`/entities/${entityId}/share-links`, { intent })).data,
  shareStats: async (code: string) =>
    (await client.get(`/share-links/${code}/stats`)).data,
  shareEvent: async (code: string, event: "review_start" | "review_submit") =>
    (await client.post(`/share-links/${code}/events`, { event })).data,
  referral: async (entityId: string) =>
    (await client.post(`/entities/${entityId}/referrals`, {}, {
      headers: { Authorization: "Subject " + (localStorage.getItem("observer_subject_session") || "") },
    })).data,
  acceptReferral: async (code: string, entityId: string) =>
    (await client.post(`/referrals/${code}/accept`, { entityId })).data,
  claimedAvatar: async (entityId: string, url: string, ownershipConfirmed: boolean) =>
    (await client.post(`/entities/${entityId}/avatar`, { url, ownershipConfirmed }, {
      headers: { Authorization: "Subject " + (localStorage.getItem("observer_subject_session") || "") },
    })).data,
  reviewInvitation: async (entityId: string, claimantAccount: string) =>
    (await client.post(`/entities/${entityId}/review-invitations`, { claimantAccount }, { headers: { Authorization: "Subject " + (localStorage.getItem("observer_subject_session") || "") } })).data,
  deliverReviewInvitation: async (invitationId: string, claimantAccount: string, emails: string[], consentConfirmed: boolean) =>
    (await client.post(`/review-invitations/${invitationId}/deliveries`, { claimantAccount, emails, consentConfirmed }, { headers: { Authorization: "Subject " + (localStorage.getItem("observer_subject_session") || "") } })).data,
  correction: async (id: string, payload: unknown) =>
    (await client.post(`/entities/${id}/corrections`, payload)).data,
  takedown: async (id: string, payload: unknown) =>
    (await client.post(`/entities/${id}/takedowns`, payload)).data,
  impersonation: async (id: string, payload: unknown) =>
    (await client.post(`/entities/${id}/impersonation-reports`, payload)).data,
  extortion: async (id: string, payload: unknown) =>
    (await client.post(`/entities/${id}/extortion-reports`, payload)).data,
  transparencyResponse: async (id: string, body: string) =>
    (await client.post(`/entities/${id}/transparency-response`, { body }, { headers: { Authorization: "Subject " + (localStorage.getItem("observer_subject_session") || "") } })).data,
  appeal: async (id: string, reason: string) =>
    (await client.post(`/change-requests/${id}/appeal`, { reason })).data,
  startEmail: async (email: string) =>
    (await client.post("/accounts/email/start", { email })).data,
  verifyEmail: async (email: string, code: string) => {
    const data = (await client.post("/accounts/email/verify", { email, code }))
      .data;
    localStorage.setItem("observer_review_session", data.sessionToken);
    localStorage.setItem("observer_account", data.accountId);
    return data;
  },
  review: async (id: string, payload: unknown) =>
    (
      await client.post(`/entities/${id}/reviews`, payload, {
        headers: {
          Authorization:
            "Observer " +
            (localStorage.getItem("observer_review_session") || ""),
        },
      })
    ).data,
  evidence: async (reviewId: string, file: File) => {
    const f = new FormData();
    f.append("evidence", file);
    return (
      await client.post(`/reviews/${reviewId}/evidence`, f, {
        headers: {
          Authorization:
            "Observer " +
            (localStorage.getItem("observer_review_session") || ""),
        },
      })
    ).data;
  },
  reply: async (reviewId: string, body: string) =>
    (
      await client.post(
        `/reviews/${reviewId}/reply`,
        { body },
        {
          headers: {
            Authorization:
              "Subject " +
              (localStorage.getItem("observer_subject_session") || ""),
          },
        },
      )
    ).data,
  dispute: async (reviewId: string, payload: unknown) =>
    (
      await client.post(`/reviews/${reviewId}/disputes`, payload, {
        headers: {
          Authorization:
            "Subject " +
            (localStorage.getItem("observer_subject_session") || ""),
        },
      })
    ).data,
  claim: async (entityId: string, payload: unknown) =>
    (await client.post(`/entities/${entityId}/claims`, payload)).data,
  verifyClaim: async (claimId: string, code: string) =>
    (await client.post(`/claims/${claimId}/verify`, { code })).data,
  startSubjectVerification: async (entityId: string, payload: unknown) =>
    (await client.post(`/entities/${entityId}/subject-verifications`, payload))
      .data,
  verifySubject: async (verificationId: string, code: string) => {
    const data = (
      await client.post(`/subject-verifications/${verificationId}/verify`, {
        code,
      })
    ).data;
    localStorage.setItem("observer_subject_session", data.sessionToken);
    return data;
  },
  event: (
    eventType: string,
    _entityId?: string,
    properties?: Record<string, string | number | boolean | string[]>,
  ) => {
    trackAnalyticsEvent(eventType, properties || {});
    return client
      .post("/analytics", {
        eventType,
        source: "client",
        properties: properties || {},
      })
      .catch(() => {});
  },
};
